import React, { Children, ReactNode, useMemo } from 'react';
import { commonUtils } from '@sato/components';
import { twMerge } from 'tailwind-merge';

import { ResolveEntry } from '@/contentful/components';

import { SectionTitle } from '@components/SectionTitle';
import { useLanguage } from '@hooks';

import { IPageSection, IPageSectionFields, ParsedEntry } from '../../types';

import { SectionLayout } from './types';
import { layoutVariants } from './Wrapper.utils';

interface Props
  extends Omit<
    ParsedEntry<IPageSection>,
    'layout' | 'content' | 'id' | 'contentType'
  > {
  id?: string;
  children?: ReactNode;
  className?: string;
  layout?: SectionLayout;
  content?: ParsedEntry<IPageSectionFields['content'][number]>[];
}

export const Section = ({
  id,
  title,
  children: childrenProp,
  content,
  layout = 'default',
  className,
  includeGap = true,
  backgroundColor,
  linkText,
  linkUrl,
  isCard,
  fullWidth,
  containerPadding,
  disableVerticalSpacing,
}: Props) => {
  const language = useLanguage();

  const children = useMemo(() => {
    const childrenList = Children.toArray(
      childrenProp ??
        content
          ?.map((entry) => {
            const element = <ResolveEntry key={entry.id} entry={entry} />;
            if (!element) return null;
            return <React.Fragment key={entry.id}>{element}</React.Fragment>;
          })
          .filter(commonUtils.isTruthy)
    );

    return {
      first: childrenList[0],
      rest: childrenList.slice(1),
    };
  }, [childrenProp, content, language]);

  // render section only if it has content
  if (![children.first, ...children.rest].length) return null;

  const showButton = linkUrl && linkText;

  return (
    <section
      id={id}
      className={twMerge(
        !disableVerticalSpacing && 'py-6',
        layoutVariants[layout],
        isCard && 'rounded-md bg-white shadow-md mdDown:!mx-0',
        containerPadding && 'p-3 md:p-5',
        className,
        fullWidth && '!mx-0 w-full'
      )}
      style={{
        backgroundColor: backgroundColor?.color,
      }}
    >
      <div className="flex-1">
        {title && (
          <SectionTitle
            {...(showButton && {
              buttonProps: {
                children: linkText,
                to: linkUrl,
              },
            })}
          >
            {title}
          </SectionTitle>
        )}

        <div
          className={twMerge(
            'flex w-full flex-col items-start',
            includeGap && 'gap-8'
          )}
        >
          {layout !== 'sidebar' && children.first}
          {children.rest}
        </div>
      </div>

      {layout === 'sidebar' && (
        <div className="lg:ml-7 lg:basis-[30rem] lg:border-l lg:border-black-light lg:pl-8">
          {children.first}
        </div>
      )}
    </section>
  );
};
